import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { classBackupApi } from "./api";
import { useConfirm } from "../../Components/Confirm";
import type {
  ImportResult,
  StaffListItem,
  SummaryResponse,
} from "./types";
import PreviewDrawer from "./PreviewDrawer";
import ConfirmAllModal from "./ConfirmAllModal";
import MapStaffDialog from "./MapStaffDialog";

// Cutoff default is 2026-07-29 per the current rollout window; admin can edit it.
const DEFAULT_CUTOFF = "2026-07-29";

const statusChip = (s: StaffListItem["status"]) => {
  const map: Record<StaffListItem["status"], { color: any; label: string }> = {
    imported:          { color: "success",  label: "Imported" },
    partial:           { color: "warning",  label: "Partial" },
    not_imported:      { color: "default",  label: "Not imported" },
    no_future_classes: { color: "info",     label: "No future classes" },
    unmapped:          { color: "error",    label: "Mapping required" },
  };
  const c = map[s] ?? { color: "default", label: s };
  return <Chip size="small" color={c.color} label={c.label} />;
};

export default function ClassBackupImportPage() {
  // Draft state — what the admin is currently typing/picking. Does NOT fire
  // requests. Only the "applied" copies drive fetches, so we don't hammer the
  // API on every keystroke.
  const [cutoffDraft, setCutoffDraft] = useState(DEFAULT_CUTOFF);
  const [searchDraft, setSearchDraft] = useState("");

  // Applied filters — the ones the fetch depends on.
  const [cutoff, setCutoff] = useState(DEFAULT_CUTOFF);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  // §8 Class Roster filter — orthogonal to the status/search/cutoff filters.
  const [rosterFilter, setRosterFilter] = useState<
    "all" | "non_empty" | "empty_only" | "none"
  >("all");

  const [items, setItems] = useState<StaffListItem[]>([]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState<Record<number, true>>({});
  const [previewFor, setPreviewFor] = useState<number | null>(null);
  const [mapFor, setMapFor] = useState<{ legacyId: number; name: string | null } | null>(null);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [processing, setProcessing] = useState<{
    total: number;
    done: number;
    current?: number;
    results: ImportResult[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Request-scoped token — a stale in-flight response whose token doesn't
  // match the latest is discarded. Prevents an older filter's result from
  // overwriting a newer one when the user rapidly changes filters/pages.
  const listReqRef = useRef(0);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const s = await classBackupApi.summary(cutoff);
      setSummary(s);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoadingSummary(false);
    }
  }, [cutoff]);

  const loadList = useCallback(async () => {
    const token = ++listReqRef.current;
    setLoadingList(true);
    try {
      const l = await classBackupApi.staffList({
        cutoff_date: cutoff, search, status: statusFilter || undefined,
        class_roster: rosterFilter,
        page, per_page: perPage,
      });
      if (token !== listReqRef.current) return; // stale
      setItems(l.items);
      setTotal(l.total);
    } catch (e: any) {
      if (token === listReqRef.current) setError(e.message || String(e));
    } finally {
      if (token === listReqRef.current) setLoadingList(false);
    }
  }, [cutoff, search, statusFilter, rosterFilter, page, perPage]);

  // Independent effects — the summary reloads on cutoff change but a
  // list-only trigger (page, status) doesn't re-hit summary.
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadList(); }, [loadList]);

  const applyFilters = () => {
    // Copy drafts → applied, reset to page 1. Both effects will fire once.
    setSearch(searchDraft);
    setCutoff(cutoffDraft);
    setPage(1);
  };

  const refresh = () => { loadSummary(); loadList(); };

  const eligibleSelected = useMemo(
    () => items.filter((i) => selected[i.legacy_staff_id] && i.eligible),
    [items, selected]
  );

  // §6 — "Update All Classes" is enabled whenever at least one selected staff
  // already has ≥1 imported live class (i.e. a follow-up sync is meaningful).
  // Never gated on filters or selection mode — works for single/multi/select-
  // visible/select-all.
  const updatableSelected = useMemo(
    () => items.filter(
      (i) => selected[i.legacy_staff_id] && i.eligible && (i.imported_classes ?? 0) > 0
    ),
    [items, selected]
  );

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = true;
      return next;
    });
  };

  const selectVisibleEligible = () => {
    const next = { ...selected };
    items.forEach((i) => { if (i.eligible) next[i.legacy_staff_id] = true; });
    setSelected(next);
  };

  const clearSelection = () => setSelected({});

  const runSequential = async (mode: "validate" | "import", legacyIds: number[]) => {
    if (legacyIds.length === 0) return;
    setError(null);
    try {
      const selection = legacyIds.length === 1 ? "single" : "multi";
      const { run_id } = await classBackupApi.startRun({
        cutoff_date: cutoff, selection_mode: selection, mode, total_staff: legacyIds.length,
      });

      setProcessing({ total: legacyIds.length, done: 0, results: [] });
      const results: ImportResult[] = [];
      for (let i = 0; i < legacyIds.length; i++) {
        const id = legacyIds[i];
        setProcessing((p) => (p ? { ...p, current: id } : p));
        try {
          const r = mode === "import"
            ? await classBackupApi.importStaff(run_id, id, cutoff)
            : await classBackupApi.validateStaff(run_id, id, cutoff);
          results.push(r);
        } catch (e: any) {
          // Continue on failure — the run is per-staff transactional server-side.
          results.push({
            staff_log_id: 0, legacy_staff_id: id, live_staff_id: null,
            status: "failed", preview: {} as any,
          });
        }
        setProcessing((p) => (p ? { ...p, done: i + 1, results: [...results] } : p));
      }

      await classBackupApi.finalizeRun(run_id);
      refresh();
    } catch (e: any) {
      setError(e.message || String(e));
    }
  };

  const confirm = useConfirm();
  const importSelected = async () => {
    if (eligibleSelected.length === 1) {
      const ok = await confirm({
        title: "Import staff?",
        message: `Import 1 staff: ${eligibleSelected[0].legacy_name}. Continue?`,
        confirmText: "Import",
        cancelText: "Cancel",
        variant: "default",
      });
      if (!ok) return;
    } else if (eligibleSelected.length > 1) {
      setConfirmAllOpen(true);
      return;
    }
    await runSequential("import", eligibleSelected.map((i) => i.legacy_staff_id));
  };

  const validateSelected = () =>
    runSequential("validate", eligibleSelected.map((i) => i.legacy_staff_id));

  // §6 — batch "Update All Classes" for the selected updatable staff. Uses
  // the /update-all endpoint which is idempotent and skips first-time creates
  // (staff without an existing live class); the frontend still starts a run
  // so audit logs line up with the other actions.
  const updateAllForSelected = async () => {
    const ids = updatableSelected.map((i) => i.legacy_staff_id);
    if (ids.length === 0) return;
    const ok = await confirm({
      title: "Update classes for selected staff?",
      message: `Update ${ids.length} staff — apply latest legacy roster/capacity/mappings to their existing live classes?`,
      confirmText: "Update all",
      cancelText: "Cancel",
      variant: "warning",
    });
    if (!ok) return;
    setError(null);
    try {
      const { run_id } = await classBackupApi.startRun({
        cutoff_date: cutoff,
        selection_mode: ids.length === 1 ? "single" : "multi",
        mode: "import",
        total_staff: ids.length,
      });
      setProcessing({ total: ids.length, done: 0, results: [] });
      const res = await classBackupApi.updateAllClasses(run_id, ids, cutoff);
      setProcessing({
        total: ids.length,
        done: ids.length,
        results: (res.items || []).map((r: any) => ({
          staff_log_id: r.staff_log_id ?? 0,
          legacy_staff_id: r.legacy_staff_id,
          live_staff_id: r.live_staff_id ?? null,
          status: r.status,
          preview: r.preview ?? ({} as any),
        })),
      });
      await classBackupApi.finalizeRun(run_id);
      refresh();
    } catch (e: any) {
      setError(e.message || String(e));
    }
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Generate Classes from Backup DB</Typography>
          <Typography variant="body2" color="text.secondary">
            Import class definitions, schedules, active student enrolments, and future class occurrences from the legacy database.
          </Typography>
        </Box>
        <IconButton onClick={refresh} title="Refresh" disabled={loadingList || loadingSummary}><RefreshOutlinedIcon /></IconButton>
      </Stack>

      <Alert severity="warning" sx={{ mb: 2 }}>
        This tool writes production class data. Import and verify a few staff members first before using <b>Select All Active Staff</b>.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={1.5} mb={2}>
        {[
          { label: "Active Legacy Staff", value: summary?.total_active },
          { label: "Eligible", value: summary?.total_eligible },
          { label: "Imported", value: summary?.imported },
          { label: "Not Imported", value: summary?.not_imported },
          { label: "Unmapped", value: summary?.unmapped },
          { label: "Errors", value: summary?.errors },
          { label: "Staff With Non-Empty Classes", value: summary?.non_empty_class_staff },
        ].map((c) => (
          <Grid key={c.label} item xs={6} sm={4} md={2}>
            <Card variant="outlined"><CardContent sx={{ py: 1.25 }}>
              <Typography variant="caption" color="text.secondary">{c.label}</Typography>
              <Typography variant="h6">{c.value ?? "–"}</Typography>
            </CardContent></Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} mb={2}>
        <TextField label="Cutoff date" type="date" size="small" value={cutoffDraft}
          onChange={(e) => setCutoffDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
          InputLabelProps={{ shrink: true }} />
        <TextField label="Search name / email" size="small" value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
          sx={{ minWidth: 240 }} />
        <TextField label="Status" size="small" select value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          sx={{ minWidth: 200 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="with_future_classes">With future classes</MenuItem>
          <MenuItem value="not_imported">Not imported</MenuItem>
          <MenuItem value="imported">Imported</MenuItem>
          <MenuItem value="unmapped">Mapping required</MenuItem>
          <MenuItem value="no_future_classes">No future classes</MenuItem>
        </TextField>
        <TextField label="Class Roster" size="small" select value={rosterFilter}
          onChange={(e) => { setRosterFilter(e.target.value as any); setPage(1); }}
          sx={{ minWidth: 220 }}>
          <MenuItem value="all">All Staff</MenuItem>
          <MenuItem value="non_empty">Has Classes With Students</MenuItem>
          <MenuItem value="empty_only">Has Empty Classes Only</MenuItem>
          <MenuItem value="none">Has No Future Classes</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={applyFilters} disabled={loadingList}>Apply</Button>
      </Stack>

      {/* Header controls */}
      <Stack direction="row" spacing={1} mb={1} alignItems="center">
        <Button size="small" onClick={selectVisibleEligible}>Select visible</Button>
        <Button size="small" onClick={clearSelection}>Clear selection</Button>
        <Box flex={1} />
        <Typography variant="body2" color="text.secondary">
          {eligibleSelected.length} eligible selected
        </Typography>
        <Button size="small" variant="outlined" onClick={validateSelected} disabled={eligibleSelected.length === 0 || !!processing}>
          Validate selected
        </Button>
        <Button size="small" variant="contained" color="primary" onClick={importSelected}
          disabled={eligibleSelected.length === 0 || !!processing}>
          Import selected
        </Button>
        {/* §6 — enabled whenever ≥1 selected eligible staff has an existing live class. */}
        <Tooltip title="Re-sync legacy roster/capacity/mappings onto existing live classes for the selected staff.">
          <span>
            <Button size="small" variant="contained" color="secondary"
              onClick={updateAllForSelected}
              disabled={updatableSelected.length === 0 || !!processing}>
              Update all classes ({updatableSelected.length})
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {processing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Processing {processing.done}/{processing.total}
          {processing.current ? ` — current legacy staff ${processing.current}` : ""}.
          {processing.done === processing.total && " Done."}
        </Alert>
      )}

      <TableContainer component={Card} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Legacy ID</TableCell>
              <TableCell>Staff Name</TableCell>
              <TableCell>Live Staff</TableCell>
              <TableCell align="center">Future Slots</TableCell>
              <TableCell align="center">Imported Classes</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingList && <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell></TableRow>}
            {!loadingList && items.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No matching staff.</TableCell></TableRow>
            )}
            {!loadingList && items.map((i) => {
              const lastResult = processing?.results.find((r) => r.legacy_staff_id === i.legacy_staff_id);
              return (
                <TableRow key={i.legacy_staff_id} hover>
                  <TableCell padding="checkbox">
                    <input type="checkbox" disabled={!i.eligible}
                      checked={!!selected[i.legacy_staff_id]}
                      onChange={() => toggle(i.legacy_staff_id)} />
                  </TableCell>
                  <TableCell>{i.legacy_staff_id}</TableCell>
                  <TableCell>
                    {i.legacy_name || "—"}
                    {i.legacy_email && (
                      <Typography variant="caption" color="text.secondary" display="block">{i.legacy_email}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {i.live_staff_id ? (
                      <>
                        <Typography variant="body2">{i.live_name || `#${i.live_staff_id}`}</Typography>
                        <Typography variant="caption" color="text.secondary">id {i.live_staff_id}</Typography>
                      </>
                    ) : (
                      <Chip size="small" color="error" label="Not mapped" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {i.has_future ? <Chip size="small" color="success" label="Yes" /> : <Chip size="small" label="—" />}
                  </TableCell>
                  <TableCell align="center">{i.imported_classes}</TableCell>
                  <TableCell>
                    {statusChip(i.status)}
                    {lastResult && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        last run: {lastResult.status}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Preview">
                      <IconButton size="small" onClick={() => setPreviewFor(i.legacy_staff_id)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!i.live_staff_id && (
                      <Tooltip title="Map to live staff">
                        <IconButton size="small" onClick={() => setMapFor({ legacyId: i.legacy_staff_id, name: i.legacy_name })}>
                          <LinkOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {i.eligible && (
                      <Tooltip title="Import this staff only">
                        <IconButton size="small" color="primary"
                          onClick={() => runSequential("import", [i.legacy_staff_id])}
                          disabled={!!processing}>
                          <PlayArrowOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" justifyContent="flex-end" alignItems="center" mt={1} spacing={1}>
        <Typography variant="caption">Page {page} of {Math.max(1, Math.ceil(total / perPage))} — {total} rows</Typography>
        <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
        <Button size="small" disabled={page * perPage >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </Stack>

      {previewFor !== null && (
        <PreviewDrawer legacyStaffId={previewFor} cutoff={cutoff} onClose={() => setPreviewFor(null)} />
      )}

      {mapFor && (
        <MapStaffDialog
          legacyId={mapFor.legacyId}
          legacyName={mapFor.name}
          onClose={() => setMapFor(null)}
          onMapped={() => { setMapFor(null); refresh(); }}
        />
      )}

      <ConfirmAllModal
        open={confirmAllOpen}
        count={eligibleSelected.length}
        onClose={() => setConfirmAllOpen(false)}
        onConfirm={async () => {
          setConfirmAllOpen(false);
          await runSequential("import", eligibleSelected.map((i) => i.legacy_staff_id));
        }}
      />
    </Box>
  );
}
